import type {
  WholeOrderEstimateType,
  WholeOrderDecisionResponseType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { useRemoteFeatureFlags } from "../../../useRemoteFeatureFlags";
import { ServerStateKeys } from "../ServerStateKeys";

export const useWholeOrderExceptionDecision = (orderId: number) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const client = useQueryClient();
  const flags = useRemoteFeatureFlags();
  const enabled = Boolean(
    user &&
    business?.Retailer &&
    flags.isActive("sdWholeOrderExceptionHandling"),
  );
  const queryKey = [user?.uid, business?.id, "wholeOrderException", orderId];
  const path = `distribution-whole-order-exceptions/buyer/orders/${orderId}`;
  const query = useQuery({
    queryKey,
    ...getDefaultQueryOptionsV2<WholeOrderDecisionResponseType | null>({
      context: { scope: "business", user, business },
      options: { enabled, retry: 2 },
    }),
    queryFn: () =>
      apiV2Request({ user, business, method: "get", path }).then(
        (res) =>
          apiV2AssertResponse<WholeOrderDecisionResponseType | null>(res).data,
      ),
  });
  const estimateQuery = useQuery({
    queryKey: [...queryKey, "estimate"],
    ...getDefaultQueryOptionsV2<WholeOrderEstimateType>({
      context: { scope: "business", user, business },
      options: {
        enabled:
          enabled &&
          query.data?.status === "pending" &&
          query.data.decisionState === "pendingBuyerDecision",
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
        retry: 2,
      },
    }),
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `${path}/estimate`,
      }).then((res) => apiV2AssertResponse<WholeOrderEstimateType>(res).data),
  });
  const mutation = useMutation({
    mutationFn: (decision: "waitForOriginalOrder" | "cancellationRequested") =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `${path}/decision`,
        data: {
          decision,
          ...(decision === "waitForOriginalOrder"
            ? {
                expectedEstimate: estimateQuery.data ?? {
                  dispatchAt: null,
                  deliveryAt: null,
                },
              }
            : {}),
        },
      }).then(
        (res) => apiV2AssertResponse<WholeOrderDecisionResponseType>(res).data,
      ),
    onError: () => {
      estimateQuery.refetch();
    },
    onSuccess: async (data) => {
      client.setQueryData(queryKey, data);
      await Promise.all([
        client.invalidateQueries({
          queryKey: [user?.uid, business?.id, ServerStateKeys.Orders],
        }),
        client.invalidateQueries({
          queryKey: [
            user?.uid,
            business?.id,
            ServerStateKeys.OrderDeliveryHistory,
            orderId,
          ],
        }),
      ]);
    },
  });
  return { query, estimateQuery, mutation, enabled };
};
